import {
  CONFIG_STATUS_LEVEL,
  ENTITY_LEVEL,
} from '../../../../common/constants/integrationConstants/crmConstants';
import { capitalizeFirstLetter } from '../../../../common/util/commonUtil';
import { checkAnyInCompleteRowInPipelineMapping } from '../../../../common/util/reducerUtils/CrmReducerUtils';
import {
  addPipelineMapping,
  addPipelineStageRow,
  cancelPipelineMapping,
  changePipelineStageRow,
  clearUnsavedChanges,
  fetchPipelineMapping,
  removePipelineMapping,
  removePipelineStageRow,
  savePipelineMapping,
} from '../../../routines/integrations/crmRoutines';

const initialState = {
  opportunity: [],
  risk: [],
  opportunityPipelineStatus: CONFIG_STATUS_LEVEL.NOT_STARTED,
  riskPipelineStatus: CONFIG_STATUS_LEVEL.NOT_STARTED,
  persistedStateOpportunity: [],
  persistedStateRisk: [],
  hasUnSavedConfig: false,
  loading: false,
  error: false,
};

const crmPipelineMappingReducer = (state = initialState, action) => {
  switch (action.type) {
    case addPipelineMapping.TRIGGER: {
      const { pipelineType, vendorPipeline } = action.payload;
      const newArrayForGivenType = [...state[pipelineType]];
      newArrayForGivenType.push({
        vendorPipeline,
        pipelineStatus: [
          {
            velarisPipelineStatus: '',
            vendorPipelineStages: [],
          },
        ],
      });
      return {
        ...state,
        [pipelineType]: newArrayForGivenType,
        hasUnSavedConfig: true,
        [`${pipelineType}PipelineStatus`]:
          checkAnyInCompleteRowInPipelineMapping(newArrayForGivenType)
            ? CONFIG_STATUS_LEVEL.IN_PROGRESS
            : CONFIG_STATUS_LEVEL.COMPLETED,
      };
    }
    case addPipelineStageRow.TRIGGER: {
      const { pipelineType, vendorPipeline } = action.payload;
      const newArrayForGivenType = state[pipelineType].map((pipeline) => {
        if (pipeline.vendorPipeline === vendorPipeline) {
          return {
            ...pipeline,
            pipelineStatus: [
              ...pipeline.pipelineStatus,
              {
                velarisPipelineStatus: '',
                vendorPipelineStages: [],
              },
            ],
          };
        }
        return pipeline;
      });
      return {
        ...state,
        [pipelineType]: newArrayForGivenType,
        hasUnSavedConfig: true,
        [`${pipelineType}PipelineStatus`]:
          checkAnyInCompleteRowInPipelineMapping(newArrayForGivenType)
            ? CONFIG_STATUS_LEVEL.IN_PROGRESS
            : CONFIG_STATUS_LEVEL.COMPLETED,
      };
    }
    case removePipelineMapping.TRIGGER: {
      const { pipelineType, vendorPipelineIndex } = action.payload;
      const newArrayForGivenType = [...state[pipelineType]];
      newArrayForGivenType.splice(vendorPipelineIndex, 1);
      return {
        ...state,
        [pipelineType]: newArrayForGivenType,
        hasUnSavedConfig: true,
        [`${pipelineType}PipelineStatus`]:
          checkAnyInCompleteRowInPipelineMapping(newArrayForGivenType)
            ? CONFIG_STATUS_LEVEL.IN_PROGRESS
            : CONFIG_STATUS_LEVEL.COMPLETED,
      };
    }

    case removePipelineStageRow.TRIGGER: {
      const { pipelineType, pipelineStageIndex, vendorPipeline } =
        action.payload;
      const newArrayForGivenType = state[pipelineType].map((pipeline) => {
        if (pipeline.vendorPipeline === vendorPipeline) {
          return {
            ...pipeline,
            pipelineStatus: pipeline.pipelineStatus.filter(
              (_, index) => index !== pipelineStageIndex
            ),
          };
        }
        return pipeline;
      });

      return {
        ...state,
        [pipelineType]: newArrayForGivenType,
        hasUnSavedConfig: true,
        [`${pipelineType}PipelineStatus`]:
          checkAnyInCompleteRowInPipelineMapping(newArrayForGivenType)
            ? CONFIG_STATUS_LEVEL.IN_PROGRESS
            : CONFIG_STATUS_LEVEL.COMPLETED,
      };
    }
    case changePipelineStageRow.TRIGGER: {
      const { pipelineType, vendorPipeline, pipelineStageIndex, side, value } =
        action.payload;
      const newArrayForGivenType = state[pipelineType].map((pipeline) => {
        if (pipeline.vendorPipeline === vendorPipeline) {
          return {
            ...pipeline,
            pipelineStatus: pipeline.pipelineStatus.map((status, index) => {
              if (index === pipelineStageIndex) {
                return {
                  ...status,
                  [side]: value,
                };
              }
              return status;
            }),
          };
        }
        return pipeline;
      });

      return {
        ...state,
        [pipelineType]: newArrayForGivenType,
        hasUnSavedConfig: true,
        [`${pipelineType}PipelineStatus`]:
          checkAnyInCompleteRowInPipelineMapping(newArrayForGivenType)
            ? CONFIG_STATUS_LEVEL.IN_PROGRESS
            : CONFIG_STATUS_LEVEL.COMPLETED,
      };
    }

    // save and fetch actions
    case fetchPipelineMapping.TRIGGER: {
      return {
        ...state,
        loading: true,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case savePipelineMapping.TRIGGER: {
      return {
        ...state,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case savePipelineMapping.SUCCESS: {
      const {
        data: { pipelineType, pipelineMappings },
      } = action.payload;

      const newState = {
        ...state,
        [pipelineType]: pipelineMappings,
        [`persistedState${capitalizeFirstLetter(pipelineType)}`]:
          pipelineMappings,
        [`${pipelineType}PipelineStatus`]: CONFIG_STATUS_LEVEL.SAVED,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
      return newState;
    }

    case fetchPipelineMapping.SUCCESS: {
      const { opportunity, risk } = action.payload;
      return {
        ...state,
        opportunity: opportunity || [],
        risk: risk || [],
        persistedStateOpportunity: opportunity || [],
        persistedStateRisk: risk || [],
        opportunityPipelineStatus: opportunity
          ? CONFIG_STATUS_LEVEL.SAVED
          : CONFIG_STATUS_LEVEL.NOT_STARTED,
        riskPipelineStatus: risk
          ? CONFIG_STATUS_LEVEL.SAVED
          : CONFIG_STATUS_LEVEL.NOT_STARTED,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }

    case savePipelineMapping.FAILURE: {
      return {
        ...state,
        error: true,
        hasUnSavedConfig: true,
      };
    }
    case fetchPipelineMapping.FAILURE: {
      return {
        ...state,
        loading: false,
        error: true,
      };
    }
    case clearUnsavedChanges.TRIGGER:
    case cancelPipelineMapping.TRIGGER: {
      const { entityTab } = action.payload;
      if (
        entityTab === ENTITY_LEVEL.OPPORTUNITY ||
        entityTab === ENTITY_LEVEL.RISK
      )
        return {
          ...state,
          [entityTab]:
            state[`persistedState${capitalizeFirstLetter(entityTab)}`],
          [`${entityTab}PipelineStatus`]: state[
            `persistedState${capitalizeFirstLetter(entityTab)}`
          ]?.length
            ? CONFIG_STATUS_LEVEL.SAVED
            : CONFIG_STATUS_LEVEL.NOT_STARTED,
          hasUnSavedConfig: false,
        };
      return { ...state };
    }
    default:
      return state;
  }
};

export default crmPipelineMappingReducer;
